<template>
  <div v-if="isVisible" class="consultation-bubble-wrapper">
    <el-button class="consultation-bubble" type="primary" @click="handleOpen">
      <i class="el-icon-chat-dot-round"></i>
      <span>{{ ct("咨询反馈", "Contact & Feedback") }}</span>
    </el-button>

    <el-drawer
      :title="ct('咨询反馈', 'Contact & Feedback')"
      :visible.sync="drawerVisible"
      direction="rtl"
      size="min(420px, 100vw)"
      :before-close="handleClose"
    >
      <div class="drawer-body">
        <el-tabs v-model="activeTab" stretch @tab-click="handleTabChange">
          <el-tab-pane :label="ct('提交咨询', 'Submit Inquiry')" name="create">
            <el-form ref="form" :model="form" :rules="rules" label-position="top">
              <el-form-item :label="ct('咨询标题', 'Subject')" prop="title">
                <el-input v-model="form.title" maxlength="100" show-word-limit :placeholder="ct('请输入咨询标题', 'Enter a subject')" />
              </el-form-item>
              <el-form-item :label="ct('咨询内容', 'Message')" prop="content">
                <el-input
                  v-model="form.content"
                  type="textarea"
                  :rows="8"
                  maxlength="2000"
                  show-word-limit
                  :placeholder="ct('请输入您的问题、意见或建议', 'Enter your question, feedback or suggestion')"
                />
              </el-form-item>
              <el-form-item :label="ct('联系方式（选填）', 'Contact Details (Optional)')" prop="contactInfo">
                <el-input v-model="form.contactInfo" maxlength="100" :placeholder="ct('手机号、邮箱或其他联系方式', 'Phone, email or other contact details')" />
              </el-form-item>
            </el-form>

            <div class="drawer-tip">
              {{ ct('提交后，我们会尽快处理。您也可以直接在右侧“我的咨询”里查看状态和回复结果。', 'We will respond as soon as possible. View the status and replies under My Inquiries.') }}
            </div>

            <div class="drawer-actions">
              <el-button @click="drawerVisible = false">{{ ct('取消', 'Cancel') }}</el-button>
              <el-button type="primary" :loading="submitting" @click="submitConsultation">{{ ct('提交咨询', 'Submit Inquiry') }}</el-button>
            </div>
          </el-tab-pane>

          <el-tab-pane :label="ct('我的咨询', 'My Inquiries')" name="list">
            <div v-loading="consultationLoading" class="consultation-list-wrap">
              <template v-if="consultationList.length">
                <div
                  v-for="item in consultationList"
                  :key="item.id"
                  class="consultation-card"
                  role="button"
                  tabindex="0"
                  :aria-label="ct('查看咨询：', 'View Inquiry: ') + item.title"
                  @click="viewConsultation(item)"
                  @keydown.enter.prevent="viewConsultation(item)"
                  @keydown.space.prevent="viewConsultation(item)"
                >
                  <div class="consultation-card-header">
                    <div class="consultation-card-title">{{ item.title }}</div>
                    <el-tag :type="statusTagType(item.status)" size="mini">{{ statusLabel(item.status) }}</el-tag>
                  </div>
                  <div class="consultation-card-time">{{ ct('提交时间：', 'Submitted: ') }}{{ item.createTime || "--" }}</div>
                  <div class="consultation-card-time">{{ ct('回复时间：', 'Replied: ') }}{{ item.replyTime || ct("暂无回复", "No reply yet") }}</div>
                  <div class="consultation-card-preview">
                    {{ item.replyContent || ct("当前暂无回复，请耐心等待。", "No reply yet. Please check back later.") }}
                  </div>
                  <div class="consultation-card-action">{{ ct('点击查看详情', 'View Details') }}</div>
                </div>
              </template>

              <div v-else class="empty-state">
                {{ ct('暂无咨询记录，您可以先在“提交咨询”里发起问题。', 'No inquiries yet. Use Submit Inquiry to ask a question.') }}
              </div>
            </div>

            <div v-if="consultationTotal > consultationQuery.size" class="pager">
              <el-pagination
                small
                background
                layout="prev, pager, next"
                :current-page="consultationQuery.page"
                :page-size="consultationQuery.size"
                :total="consultationTotal"
                @current-change="handleConsultationPageChange"
              />
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>

      <el-dialog :title="ct('咨询详情', 'Inquiry Details')" :visible.sync="detailVisible" width="min(720px, calc(100vw - 32px))" append-to-body>
        <div v-if="currentConsultation" class="consultation-detail">
          <div class="detail-block">
            <div class="detail-label">{{ ct('咨询标题', 'Subject') }}</div>
            <div class="detail-value">{{ currentConsultation.title }}</div>
          </div>
          <div class="detail-block">
            <div class="detail-label">{{ ct('咨询内容', 'Message') }}</div>
            <div class="detail-value">{{ currentConsultation.content }}</div>
          </div>
          <div class="detail-grid">
            <div class="detail-block">
              <div class="detail-label">{{ ct('联系方式', 'Contact Details') }}</div>
              <div class="detail-value">{{ currentConsultation.contactInfo || ct("未填写", "Not provided") }}</div>
            </div>
            <div class="detail-block">
              <div class="detail-label">{{ ct('状态', 'Status') }}</div>
              <div class="detail-value">{{ statusLabel(currentConsultation.status) }}</div>
            </div>
            <div class="detail-block">
              <div class="detail-label">{{ ct('提交时间', 'Submitted') }}</div>
              <div class="detail-value">{{ currentConsultation.createTime }}</div>
            </div>
            <div class="detail-block">
              <div class="detail-label">{{ ct('回复时间', 'Replied') }}</div>
              <div class="detail-value">{{ currentConsultation.replyTime || ct("暂无回复", "No reply yet") }}</div>
            </div>
          </div>
          <div class="detail-block">
            <div class="detail-label">{{ ct('管理员回复', 'Staff Reply') }}</div>
            <div class="detail-value reply-box">{{ currentConsultation.replyContent || ct("当前暂无回复，请耐心等待。", "No reply yet. Please check back later.") }}</div>
          </div>
        </div>
      </el-dialog>
    </el-drawer>
  </div>
</template>

<script>
import { loginLocation } from "@/utils/authSession";
import { handleRequestFailure } from "@/utils/http";
import {
  PostConsultationCreateApi,
  PostConsultationDetailApi,
  PostConsultationPageApi,
} from "@/utils/api";

export default {
  name: "ConsultationBubble",
  data() {
    return {
      drawerVisible: false,
      activeTab: "create",
      submitting: false,
      consultationLoading: false,
      consultationList: [],
      consultationTotal: 0,
      consultationQuery: {
        page: 1,
        size: 5,
      },
      detailVisible: false,
      currentConsultation: null,
      form: {
        title: "",
        content: "",
        contactInfo: "",
      },
    };
  },
  computed: {
    rules() {
      return {
        title: [{ required: true, message: this.ct("请输入咨询标题", "Enter a subject"), trigger: "blur" }],
        content: [{ required: true, message: this.ct("请输入咨询内容", "Enter your message"), trigger: "blur" }],
      };
    },
    isVisible() {
      const hiddenRoutes = ["/login", "/register", "/forgetPwd"];
      return !hiddenRoutes.includes(this.$route.path);
    },
  },
  mounted() {
    this.$root.$on("open-strain-consultation", this.openStrainConsultation);
  },
  beforeDestroy() {
    this.$root.$off("open-strain-consultation", this.openStrainConsultation);
  },
  methods: {
    ct(chinese, english) {
      const locale = this.$i18n ? this.$i18n.locale : localStorage.getItem("lang");
      return locale === "en" ? english : chinese;
    },
    statusLabel(status) {
      const labels = { "待处理": "Pending", "处理中": "In Progress", "已回复": "Replied" };
      return this.ct(status, labels[status] || status);
    },
    handleOpen() {
      if (!localStorage.getItem("token")) {
        this.$message.warning(this.ct("请先登录后再提交咨询", "Please log in before submitting an inquiry."));
        this.$router.push(loginLocation(this.$route.fullPath));
        return false;
      }
      this.drawerVisible = true;
      if (this.activeTab === "list") {
        this.loadConsultations();
      }
      return true;
    },
    async openStrainConsultation(strain) {
      if (!strain || !strain.id || this.submitting) return;
      if (!localStorage.getItem("token")) {
        this.handleOpen();
        return;
      }
      // Only public identifiers belong in the editable consultation draft.
      const identifier = strain.num || strain.id;
      const title = `${this.ct("品系咨询：", "Strain Inquiry: ")}${identifier}`.slice(0, 100);
      const content = `${this.ct("品系编号：", "Strain ID: ")}${identifier}\n${this.ct("品系名称：", "Strain Name: ")}${strain.fullName || this.ct("未提供", "Not provided")}\n\n${this.ct("咨询问题：", "Question: ")}`.slice(0, 2000);
      if ((this.form.title || this.form.content)
        && (this.form.title !== title || this.form.content !== content)) {
        try {
          await this.$confirm(this.ct("已有未提交的咨询内容，是否替换为当前品系？", "Replace your unsent draft with this strain?"), this.ct("切换咨询品系", "Change Strain"), {
            confirmButtonText: this.ct("替换", "Replace"),
            cancelButtonText: this.ct("保留原内容", "Keep Draft"),
            type: "warning",
          });
        } catch (_) {
          return;
        }
      }
      if (this.submitting || !localStorage.getItem("token")) return;
      this.form.title = title;
      this.form.content = content;
      this.activeTab = "create";
      this.handleOpen();
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate();
      });
    },
    handleClose(done) {
      done();
    },
    handleTabChange() {
      if (this.activeTab === "list") {
        this.consultationQuery.page = 1;
        this.loadConsultations();
      }
    },
    submitConsultation() {
      this.$refs.form.validate((valid) => {
        if (!valid) {
          return false;
        }
        this.submitting = true;
        PostConsultationCreateApi(this.form).then((res) => {
          if (res && res.code === 200) {
            this.$message.success(this.ct("已提交，我们会尽快处理", "Submitted. We will respond as soon as possible."));
            this.resetForm();
            this.activeTab = "list";
            this.consultationQuery.page = 1;
            this.loadConsultations();
          }
        }).finally(() => {
          this.submitting = false;
        }).catch(handleRequestFailure);
      });
    },
    loadConsultations() {
      this.consultationLoading = true;
      PostConsultationPageApi(this.consultationQuery).then((res) => {
        if (res && res.code === 200) {
          this.consultationList = res.data.content || [];
          this.consultationTotal = res.data.totalElements || 0;
        }
      }).finally(() => {
        this.consultationLoading = false;
      }).catch(handleRequestFailure);
    },
    handleConsultationPageChange(page) {
      this.consultationQuery.page = page;
      this.loadConsultations();
    },
    viewConsultation(row) {
      PostConsultationDetailApi({ id: row.id }).then((res) => {
        if (res && res.code === 200) {
          this.currentConsultation = res.data;
          this.detailVisible = true;
        }
      }).catch(handleRequestFailure);
    },
    statusTagType(status) {
      if (status === "已回复") return "success";
      if (status === "处理中") return "warning";
      return "info";
    },
    resetForm() {
      this.form = {
        title: "",
        content: "",
        contactInfo: "",
      };
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.consultation-bubble-wrapper {
  position: fixed;
  right: 24px;
  bottom: 160px;
  z-index: 3000;
}

.consultation-bubble {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 999px;
  box-shadow: 0 10px 24px rgba(64, 112, 244, 0.25);
}

.drawer-body {
  padding: 0 8px 24px;
}

.drawer-tip {
  margin-top: 8px;
  font-size: 13px;
  line-height: 20px;
  color: #909399;
}

.drawer-actions {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

.consultation-list-wrap {
  min-height: 280px;
}

.consultation-card {
  padding: 14px;
  margin-bottom: 12px;
  border: 1px solid #ebeef5;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
}

.consultation-card:hover {
  border-color: #95b1ff;
  box-shadow: 0 8px 20px rgba(64, 112, 244, 0.12);
}

.consultation-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.consultation-card-title {
  flex: 1;
  font-size: 15px;
  font-weight: 600;
  line-height: 22px;
  color: #1f2d3d;
}

.consultation-card-time {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
}

.consultation-card-preview {
  margin-top: 10px;
  font-size: 13px;
  line-height: 20px;
  color: #606266;
  line-clamp: 2;
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.consultation-card-action {
  margin-top: 10px;
  font-size: 12px;
  color: #4070f4;
}

.empty-state {
  padding: 48px 16px;
  text-align: center;
  font-size: 13px;
  line-height: 22px;
  color: #909399;
  border: 1px dashed #dcdfe6;
  border-radius: 12px;
}

.pager {
  margin-top: 16px;
  text-align: right;
}

.consultation-detail {
  .detail-block + .detail-block {
    margin-top: 16px;
  }
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.detail-label {
  margin-bottom: 8px;
  font-size: 13px;
  color: #909399;
}

.detail-value {
  line-height: 22px;
  color: #303133;
  word-break: break-word;
}

.reply-box {
  padding: 12px;
  background: #f7f9fc;
  border-radius: 8px;
}

@media (max-width: 768px) {
  .consultation-bubble-wrapper {
    right: 16px;
    bottom: 96px;
  }

  .consultation-bubble {
    padding: 10px 14px;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>
